import { SpaceType } from "@components/atoms/Spacer/types";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import styled from "styled-components/native";
import { useSelectedItems } from "../../contexts/SelectedItemsContext";
import { useQueryClient } from "@tanstack/react-query";
import useScreenOptionsHooks from "../../hooks/useScreenOptionsHooks";
import { useTranslation } from "react-i18next";
import { RequestType } from "@scenes/bulk-request/Utils";
import { capitalizeFirstLetter } from "@utils/stringUtils";
import { ActiveClickTypes } from "./types";
import { compareFormValues, RequestTypes } from "./utils/utils";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { Platform, View } from "react-native";
import { ButtonType } from "@components/atoms/Button/types";
import { Button } from "@components/atoms";
import { useBulkRequestState } from "./context/BulkStateContext";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { css } from "styled-components/native";
import { SearchFilterTypeEnumLowercase } from "@components/atoms/Filters/utils";
import {
  formatNumberWithPrecision,
  removePercentage,
} from "@utils/product/productFormatters";

const ButtonViewContainer = forwardRef((props: any, ref) => {
  const {
    stockItemRequest,
    customForm,
    productType,
    isBulkRequest,
    requestType,
    showConfirmPopup,
    modifyRequest,
    navigation,
    dataBuy,
    dataMemo,
    dataHold,
    itemList,
    priceMode,
    disableRequest,
    setShowConfirmModifyPopup,
    setIsDirty,
  } = props;
  const { t } = useTranslation();
  const { theme } = useScreenOptionsHooks();
  const buttonWidth = theme.isMobile ? "auto" : 160;
  const { selectedItems } = useSelectedItems();
  const hasChildren = !!stockItemRequest?.children?.length;
  const queryClient = useQueryClient();
  const { setItems } = useBulkRequestState() || {};
  const {
    dynamicFormValues: customFormData,
    isDynamicFormValid: customFormValidation,
    isDirtyForm,
  } = useDynamicFormState();
  const [localForm, setLocalForm] = useState(customFormData);
  useEffect(() => {
    setLocalForm(customFormData);
  }, []);

  const removeCommaFromPrice = (price: string) => {
    return price?.replaceAll(",", "");
  };

  useImperativeHandle(ref, () => ({
    handleRequest,
  }));

  const handleSuccess = (msg?: string) => {
    if (msg) showToast("Success", msg, ToastTypes.SUCCESS, true, true);
    if (navigation.canGoBack()) navigation.goBack();
  };

  useEffect(() => {
    setIsDirty?.(isDirtyForm || compareFormValues(localForm, customForm));
  }, [isDirtyForm, customFormData]);

  const handleRequest = () => {
    const isLooseParcel =
      itemList?.length > 0
        ? itemList?.[0]?.diamond_category ===
          SearchFilterTypeEnumLowercase.LOOSE_PARCEL
        : stockItemRequest[productType.productType]?.diamond_category ===
          SearchFilterTypeEnumLowercase.LOOSE_PARCEL;

    const parcel_offer_price = itemList.reduce(
      (totalCount: number, item: any) => {
        return totalCount + Number(item?.updated_total_sales_price ?? 0);
      },
      0
    );

    let payload = {};
    if (isBulkRequest) {
      payload = {
        bulk_request: {
          ...(requestType?.toLowerCase() === RequestType.BUY && {
            comments: customForm
              ? customFormData.requestForm.comments
              : dataBuy.buyComments,
          }),
          ...(requestType?.toLowerCase() === RequestType.MEMO && {
            comments: customForm
              ? customFormData.requestForm.comments
              : dataMemo.memoComments,
          }),
          ...(requestType?.toLowerCase() === RequestType.HOLD && {
            comments: customForm
              ? customFormData.requestForm.comments
              : dataHold.holdComments,
            hold_hours: customForm
              ? customFormData.requestForm.hold_hours
              : dataHold.hold_hours,
          }),
          stock_item_request_id: stockItemRequest?.id,
          stock_item_type: capitalizeFirstLetter(productType?.productType),
          price_mode: priceMode,
          stock_item_request: itemList.map((item) => ({
            offer_price_per_carat: customForm
              ? item?.updated_price_per_carat || item?.price_per_carat
              : item?.offer_price_per_carat,
            offer_total_sales_price: customForm
              ? item?.updated_total_sales_price || item?.total_sales_price
              : removeCommaFromPrice(String(item.offer_total_sales_price)),
            offer_discount_percent: customForm
              ? removePercentage(item?.updated_discount_percent) ||
                item?.discount_percent
              : removeCommaFromPrice(String(item.offer_discount_percent)),
            stock_item_request_id: customForm
              ? stockItemRequest.children.find(
                  (obj) => obj?.[productType.productType].id === item?.id
                ).id
              : item?.stock_item_request_id,
            ...(item?.quantity && { quantity: item.quantity }),
            ...(item?.offer_size && {
              offer_size: item.offer_size,
              offer_total_sales_price: item.updated_total_sales_price,
            }),
            comments: item?.updated_comments || item.comments,
          })),
          ...(requestType?.toLowerCase() === RequestType.BUY &&
            ((customForm &&
              customFormData?.requestForm?.offerTotalPrice !== undefined) ||
              (!customForm && dataBuy?.priceOffer !== undefined)) && {
              offer_total_sales_price: removeCommaFromPrice(
                String(
                  customForm
                    ? customFormData?.totalOfferPrice ||
                        customFormData?.requestForm?.offerTotalPrice
                    : dataBuy.priceOffer
                )
              ),
            }),
          ...(isLooseParcel && { offer_total_sales_price: parcel_offer_price }),
          ...(requestType?.toLowerCase() === RequestType.BUY && {
            pay_within: customForm
              ? customFormData.requestForm?.payWithin
              : dataBuy.payWithIn,
            offer_discount_percent:
              removePercentage(
                customFormData?.requestForm.offerTotalDiscount
              ) ?? "",
          }),
          ...(requestType?.toLowerCase() === RequestType.BUY && {
            payment_type: customForm
              ? customFormData.requestForm?.radioSelect
                ? customFormData.requestForm?.radioSelect.toLowerCase()
                : "Invoice"
              : dataBuy.selectedRadio?.toLowerCase(),
          }),
          request_type:
            requestType !== RequestTypes.Memo
              ? `bulk_${requestType?.toLowerCase()}_${productType.productType}`
              : `bulk_${productType.productType}_${requestType?.toLowerCase()}`,
          additional_attributes: {
            total_offer_price_applied: false,
          },
        },
      };
      modifyRequest.mutate(payload, {
        onSuccess() {
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request"] })
            .then();
          handleSuccess(t("requestHistoryDetail.bulkRequestModified.text"));
          setShowConfirmModifyPopup(false);
          setItems([]);
        },
      });
    } else {
      if (requestType?.toLowerCase() === "buy") {
        payload = {
          payment_type: customForm
            ? customFormData.singleForm?.[
                stockItemRequest[productType.productType]?.id
              ].radioSelect?.toLowerCase()
            : dataBuy.selectedRadio,
          pay_within: customForm
            ? customFormData.singleForm?.[
                stockItemRequest[productType.productType]?.id
              ].payWithin
            : dataBuy.payWithIn,
          ...(productType.productType !== "jewelry" && {
            offer_price_per_carat: isLooseParcel
              ? Math.ceil(
                  Number(
                    stockItemRequest[productType.productType]?.price_per_carat
                  )
                )
              : removeCommaFromPrice(
                  String(
                    customForm
                      ? customFormData.singleForm?.[
                          stockItemRequest[productType.productType]?.id
                        ].pricePerOffer
                      : dataBuy.ctOffer
                  )
                ),
          }),
          offer_total_sales_price: removeCommaFromPrice(
            customForm
              ? isLooseParcel
                ? formatNumberWithPrecision(
                    Math.ceil(
                      Number(
                        stockItemRequest[productType.productType]
                          ?.price_per_carat
                      )
                    ) *
                      Number(
                        customFormData.singleForm?.[
                          stockItemRequest[productType.productType]?.id
                        ].requestedTotalCarat?.replace(" ct", "")
                      ),
                    { precision: 2 }
                  )
                : customFormData.singleForm?.[
                    stockItemRequest[productType.productType]?.id
                  ].totalOfferPrice
              : String(dataBuy.priceOffer)
          ),
          offer_discount_percent: removeCommaFromPrice(
            customForm
              ? customFormData.singleForm?.[
                  stockItemRequest[productType.productType]?.id
                ].totalOfferDiscount
              : String(dataBuy.discountPercent)
          ),
          comments: customForm
            ? customFormData.singleForm?.[
                stockItemRequest[productType.productType]?.id
              ].comments
            : dataBuy.buyComments,
          quantity: removeCommaFromPrice(
            customForm
              ? customFormData.singleForm?.[
                  stockItemRequest[productType.productType]?.id
                ].requestedQuantity ?? stockItemRequest.quantity?.toString()
              : stockItemRequest.quantity?.toString() || ""
          ),
          offer_size: removeCommaFromPrice(
            customForm
              ? customFormData.singleForm?.[
                  stockItemRequest[productType.productType]?.id
                ].requestedTotalCarat?.replace(" ct", "")
              : stockItemRequest.offer_size || ""
          ),
        };
      } else if (requestType?.toLowerCase() === "memo") {
        payload = {
          comments: customForm
            ? customFormData.singleForm?.[
                stockItemRequest[productType.productType]?.id
              ].comments
            : dataMemo.memoComments,
          ...(stockItemRequest.quantity?.toString() && {
            quantity: stockItemRequest.quantity?.toString(),
          }),
        };
      } else if (requestType?.toLowerCase() === "hold") {
        payload = {
          comments: customForm
            ? customFormData.singleForm?.[
                stockItemRequest[productType.productType]?.id
              ].comments
            : dataHold.holdComments,
          hold_hours: customForm
            ? customFormData.singleForm?.[
                stockItemRequest[productType.productType]?.id
              ].hold_hours
            : dataHold.hold_hours,
          ...(stockItemRequest.quantity?.toString() && {
            quantity: stockItemRequest.quantity?.toString(),
          }),
        };
      }

      modifyRequest.mutate(
        {
          stock_item_request: payload,
        },
        {
          onSuccess() {
            queryClient
              .invalidateQueries({ queryKey: ["stock_item_request"] })
              .then();
            handleSuccess(t("requestHistoryDetail.requestModified.text"));
            setShowConfirmModifyPopup(false);
          },
        }
      );
    }
  };

  return (
    <ButtonView>
      <ButtonContainer>
        <ButtonFlexContainer>
          <Button
            type={ButtonType.secondary}
            width={buttonWidth}
            category={productType.theme}
            title={t("requestHistoryDetail.buttons.withdraw.text")}
            disabled={hasChildren ? !selectedItems.length : false}
            onPress={() => {
              showConfirmPopup(ActiveClickTypes.WITHDRAW);
            }}
          />
        </ButtonFlexContainer>
        <ButtonFlexContainer>
          <Button
            type={ButtonType.primary}
            width={buttonWidth}
            category={productType.theme}
            disabled={
              (customForm
                ? customFormValidation?.requestForm?.isValid === undefined
                  ? !customFormValidation?.singleForm?.isValid
                  : !customFormValidation?.requestForm?.isValid
                : disableRequest ||
                  (requestType === RequestTypes.Buy &&
                    (!dataBuy.ctOffer || !dataBuy.priceOffer))) ||
              (hasChildren ? !selectedItems.length : false)
            }
            title={t("requestHistoryDetail.buttons.submit.text")}
            onPress={() => setShowConfirmModifyPopup(true)}
          />
        </ButtonFlexContainer>
      </ButtonContainer>
      {theme.isMobile && Platform.OS === "ios" && (
        <View style={{ height: 34 }} />
      )}
    </ButtonView>
  );
});

export default ButtonViewContainer;

const ButtonView = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  padding: ${SpaceType.Big}px ${SpaceType.Double}px;
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row-reverse")};
  justify-content: space-evenly;
  border-radius: ${({ theme }) => (theme.isMobile ? "0" : "8px")};

  ${({ theme }) =>
    theme.isMobile &&
    css`
      border-top-width: 1px;
      border-top-color: ${theme["core-03"]};
    `}
`;

const ButtonContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: ${SpaceType.Basic}px;
`;

const ButtonFlexContainer = styled.View`
  flex: ${({ theme }) => (theme.isMobile ? "1" : "none")};
  padding-horizontal: 6px;
`;
